import * as React from 'react'
import { useInRouterContext, useSearchParams } from 'react-router-dom'

import createContext from 'modules/core/hooks/createContext'
import { useCallback } from 'modules/core/hooks/useCallback'

import type { FiltersDispatch } from './reducers/filter'
import FilterReducer, { FilterState, initialFiltersState } from './reducers/filter'

type Context = [FilterState, FiltersDispatch]

const { context: FilterContext, hook: useFilters } = createContext<Context>()

function parseEmployeeIds(param: string | null): string[] | null {
  if (!param) return null
  const ids = param
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
  return ids.length > 0 ? [...new Set(ids)] : null
}

function initialFiltersStateFromSearchParams(searchParams: URLSearchParams): FilterState {
  const employeeIdsParam = searchParams.get('_ai_employees')
  const locationIdParam = searchParams.get('_ai_location')
  const teamIdParam = searchParams.get('_ai_team')

  const hasAiFilters = employeeIdsParam || locationIdParam || teamIdParam
  if (!hasAiFilters) return initialFiltersState()

  return {
    ...initialFiltersState(),
    employeeIds: parseEmployeeIds(employeeIdsParam),
    locationIds: locationIdParam ? [locationIdParam] : [],
    teamIds: teamIdParam ? [teamIdParam] : null,
  }
}

/**
 * Inner provider that uses useSearchParams — only rendered when inside a Router context.
 * This avoids crashing tests that render ShiftManagementProviders without a Router.
 */
const FiltersProviderWithRouter = ({ children }: React.PropsWithChildren<object>) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const memoizedReducer = useCallback(FilterReducer, [])
  const [state, dispatch] = React.useReducer(
    memoizedReducer,
    searchParams,
    initialFiltersStateFromSearchParams
  )

  // Track whether this is the first effect run — on mount the reducer initializer
  // already applied any _ai_* params present in the URL, so we skip the dispatch
  // to avoid a redundant re-render. Subsequent runs (params added after mount) do dispatch.
  const isMountRef = React.useRef(true)

  React.useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false

      // Clean up _ai_* params from the URL if they were present on initial load,
      // without dispatching again (reducer initializer already handled them).
      const hasAiFiltersOnMount =
        searchParams.get('_ai_employees') ||
        searchParams.get('_ai_location') ||
        searchParams.get('_ai_team')

      if (hasAiFiltersOnMount) {
        const next = new URLSearchParams(searchParams)
        next.delete('_ai_employees')
        next.delete('_ai_location')
        next.delete('_ai_team')
        setSearchParams(next, { replace: true })
      }
      return
    }

    const employeeIdsParam = searchParams.get('_ai_employees')
    const locationIdParam = searchParams.get('_ai_location')
    const teamIdParam = searchParams.get('_ai_team')

    const hasAiFilters = employeeIdsParam || locationIdParam || teamIdParam
    if (!hasAiFilters) return

    // Apply the AI filters via dispatch when params are added after mount
    // (e.g. FilterEmployeeListActivity navigates with _ai_* params).
    dispatch({
      type: 'FILTER_BY_AI',
      employeeIds: parseEmployeeIds(employeeIdsParam),
      locationIds: locationIdParam ? [locationIdParam] : null,
      teamIds: teamIdParam ? [teamIdParam] : null,
    })

    // Remove the _ai_* params from the URL so they don't persist on reload.
    const next = new URLSearchParams(searchParams)
    next.delete('_ai_employees')
    next.delete('_ai_location')
    next.delete('_ai_team')
    setSearchParams(next, { replace: true })
  }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

  return <FilterContext.Provider value={[state, dispatch]}>{children}</FilterContext.Provider>
}

const FiltersProviderWithoutRouter = ({ children }: React.PropsWithChildren<object>) => {
  const memoizedReducer = useCallback(FilterReducer, [])
  const [state, dispatch] = React.useReducer(memoizedReducer, initialFiltersState())

  return <FilterContext.Provider value={[state, dispatch]}>{children}</FilterContext.Provider>
}

const FiltersProvider = ({ children }: React.PropsWithChildren<object>) => {
  const inRouterContext = useInRouterContext()

  return inRouterContext ? (
    <FiltersProviderWithRouter>{children}</FiltersProviderWithRouter>
  ) : (
    <FiltersProviderWithoutRouter>{children}</FiltersProviderWithoutRouter>
  )
}

export { FiltersProvider, useFilters }
